"use client";

import { useState, useEffect } from "react";
import {
  asMDICaseID,
  asMDIPatientID,
  Case,
  MDICaseID,
  checkPatientHasUnreadMessages,
  UnreadMessageInfo,
} from "@repo/provider-network";

/**
 * Hook to check for unread messages for active visits
 * Checks messages whenever SWR revalidates (every 5 seconds)
 */
export function useUnreadMessages(
  visits?: { current: Case[]; past: Case[] },
  isValidating?: boolean
) {
  const [unreadMap, setUnreadMap] = useState<Map<MDICaseID, UnreadMessageInfo>>(
    new Map()
  );
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    if (!visits || !visits.current || visits.current.length === 0) {
      return;
    }

    // Check messages whenever SWR is validating (which includes initial load)
    if (!isValidating) {
      return;
    }

    const checkUnreadForActiveVisits = async () => {
      setIsChecking(true);
      const newUnreadMap = new Map<MDICaseID, UnreadMessageInfo>();

      // Check both active and waiting visits for unread messages
      const visitsToCheck = visits.current.filter((v) => {
        const status = v.case_status?.name?.toLowerCase();
        return status === "active" || status === "waiting";
      });

      // Check each visit for unread messages
      await Promise.all(
        visitsToCheck.map(async (visit) => {
          if (visit.patient?.patient_id && visit.case_id) {
            try {
              const unreadInfo = await checkPatientHasUnreadMessages(
                asMDIPatientID(visit.patient.patient_id)
              );
              newUnreadMap.set(asMDICaseID(visit.case_id), unreadInfo);
            } catch (error) {
              console.error(
                "Error checking unread for visit:",
                visit.case_id,
                error
              );
              newUnreadMap.set(asMDICaseID(visit.case_id), {
                hasUnread: false,
              });
            }
          }
        })
      );

      setUnreadMap(newUnreadMap);
      setIsChecking(false);
    };

    checkUnreadForActiveVisits();
  }, [visits, isValidating]);

  return { unreadMap, isChecking };
}
